import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  ArrowUpRight,
  BarChart3,
  Box,
  CheckCircle2,
  ClipboardList,
  IndianRupee,
  Layers,
  MapPin,
  Navigation,
  Package,
  Plus,
  Settings,
  ShieldCheck,
  ShoppingBag,
  Star,
  TrendingUp,
  Truck,
  Wallet,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Badge, type BadgeTone, Spinner } from '@terra/ui';
import { formatPriceShort } from '@terra/utils';

import { DashboardOrdersDrawer, type DashboardDrill } from '../components/DashboardOrdersDrawer';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';

// ── Utilities ──────────────────────────────────────────────────────
const formatPaise = (p: number) =>
  `₹${((p || 0) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

/** Indian dashboard figure: ₹2,435 · ₹1.25 L · ₹2.5 Cr (never "K"). */
const compactInr = (p: number) => formatPriceShort(p || 0);

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.5, ease: [0.16, 1, 0.3, 1] as const },
  }),
};

const PRODUCT_STATUS_TONE: Record<string, BadgeTone> = {
  DRAFT: 'neutral',
  PENDING_APPROVAL: 'warning',
  CHANGES_REQUESTED: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  ARCHIVED: 'neutral',
};

function Skeleton({ className }: { className?: string }) {
  return <div className={`animate-pulse rounded-md bg-slate-200/60 ${className}`} />;
}

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * The parcel lifecycle, grouped into tiles a seller can act on.
 *
 * Every one of the twelve shipment statuses appears in exactly one tile —
 * omitting a status would silently drop parcels out of the totals, and a seller
 * counting rows that do not add up trusts none of them.
 *
 * `attention` marks the tiles that mean somebody has to do something.
 */
const SHIPMENT_TILES: Array<{ label: string; keys: string[]; attention?: boolean }> = [
  { label: 'Pending', keys: ['PENDING'] },
  { label: 'AWB Assigned', keys: ['AWB_ASSIGNED'] },
  { label: 'Pickup', keys: ['PICKUP_SCHEDULED', 'PICKED_UP'] },
  { label: 'In Transit', keys: ['IN_TRANSIT'] },
  { label: 'Out for Delivery', keys: ['OUT_FOR_DELIVERY'] },
  { label: 'Delivered', keys: ['DELIVERED'] },
  // A failed attempt goes back to origin at the seller's cost unless somebody
  // acts before the courier's attempt budget runs out. RTO sits alongside it
  // because by then that is what has already started happening.
  { label: 'NDR / RTO', keys: ['UNDELIVERED', 'RTO_INITIATED', 'RTO_DELIVERED'], attention: true },
  // A booking that failed never reached a courier at all, which looks identical
  // to "nothing happened yet" unless it is named.
  { label: 'Cancelled / Failed', keys: ['CANCELLED', 'FAILED'], attention: true },
];

/** Statuses worth colouring in the recent list, for the same reason. */
const ATTENTION_STATUSES = new Set([
  'UNDELIVERED',
  'RTO_INITIATED',
  'RTO_DELIVERED',
  'FAILED',
  'CANCELLED',
]);

// ── Component ──────────────────────────────────────────────────────
export function OverviewPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  // The tile whose orders the side drawer is showing, if any.
  const [drill, setDrill] = useState<DashboardDrill | null>(null);
  // The fulfilment tiles count this calendar month, as the summary API does.
  const now = new Date();
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;

  // ── Queries ──
  const { data: productsData, isLoading: isLoadingProducts } = useQuery({
    queryKey: ['vendor-products-dashboard'],
    queryFn: () => api.products.myProducts({ pageSize: 12 }),
  });

  const { data: kycData } = useQuery({
    queryKey: ['kyc-status-dashboard'],
    queryFn: () => api.users.getKycStatus(),
  });

  const { data: healthData } = useQuery({
    queryKey: ['vendor-health-dashboard'],
    queryFn: () => api.intelligence.myVendor(),
  });

  const { data: ratingsData } = useQuery({
    queryKey: ['vendor-ratings-dashboard'],
    queryFn: () => api.vendors.ratings(),
  });

  // Totals, the monthly sales chart, the category mix and shipment counts — all
  // computed in the database, so no figure here depends on a capped page.
  const { data: summary, isLoading: isLoadingSummary } = useQuery({
    queryKey: ['vendor-dashboard-summary'],
    queryFn: () => api.vendors.myDashboardSummary(),
  });

  // ── Metrics Calculations ──
  const products = productsData?.data ?? [];
  const totalProducts = productsData?.page?.total ?? products.length;

  const totalOrders = summary?.orders.total ?? 0;
  const totalSalesPaise = summary?.orders.lifetimeSalesPaise ?? 0;

  // Ratings
  const vendorRatingObj = ratingsData?.find((r) => r.id === healthData?.vendorId);
  const avgRating = vendorRatingObj ? (vendorRatingObj.avgRatingX100 / 100).toFixed(1) : '0.0';
  const reviewCount = vendorRatingObj?.reviewCount ?? 0;

  // Month-on-month and the fulfilment breakdown, both straight from the API.
  //
  // These were computed here by filtering the fetched order page. That page is
  // capped at 100 rows, so past a hundred orders every figure below silently
  // understated itself — and understated in the direction that looks fine.
  const currentMonthSalesPaise = summary?.orders.thisMonth.salesPaise ?? 0;
  const lastMonthSalesPaise = summary?.orders.lastMonth.salesPaise ?? 0;

  const newOrdersCount = summary?.orders.thisMonth.newOrders ?? 0;
  const cancelledOrdersCount = summary?.orders.thisMonth.cancelled ?? 0;
  const onDeliveryCount = summary?.orders.thisMonth.onDelivery ?? 0;
  const deliveredCount = summary?.orders.thisMonth.delivered ?? 0;

  // Category distribution — counted by the server over the whole catalogue,
  // not the handful of products this screen lists.
  const categoryMix = summary?.categories ?? [];
  const categoryTotal = categoryMix.reduce((n, c) => n + c.count, 0);
  const categoryCounts = categoryMix.slice(0, 5).map((c) => ({
    name: c.name,
    count: c.count,
    percentage: categoryTotal ? (c.count / categoryTotal) * 100 : 0,
  }));

  const categoryColors = ['#6C63FF', '#10B981', '#F59E0B', '#EF4444', '#3B82F6'];

  // Revenue chart data (past 6 months)
  // Summed per month by the server, so every order counts — not just the
  // latest page of them.
  const chartData = (summary?.orders.monthly ?? []).map((m) => ({
    name: months[Number(m.month.slice(5, 7)) - 1] ?? m.month,
    revenue: m.salesPaise,
  }));

  const kycStatus = kycData?.status ?? 'NOT_SUBMITTED';
  const loading = isLoadingProducts || isLoadingSummary;

  // Quick actions
  const quickActions = [
    {
      to: '/products',
      label: 'Catalog',
      desc: 'Manage products',
      Icon: Package,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
    },
    {
      to: '/orders',
      label: 'Orders',
      desc: 'Fulfillment & tracking',
      Icon: ShoppingBag,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
    },
    {
      to: '/wallet',
      label: 'Wallet',
      desc: 'Earnings & payouts',
      Icon: Wallet,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
    },
    {
      to: '/analytics',
      label: 'Analytics',
      desc: 'Business insights',
      Icon: BarChart3,
      color: 'text-indigo-600',
      bg: 'bg-indigo-50',
    },
    {
      to: '/inventory',
      label: 'Inventory',
      desc: 'Stock management',
      Icon: Layers,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      to: '/onboarding',
      label: 'Settings',
      desc: 'Store configuration',
      Icon: Settings,
      color: 'text-rose-600',
      bg: 'bg-rose-50',
    },
  ];

  if (loading) {
    return (
      <div className="grid min-h-[400px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12 font-sans text-slate-900">
      {/* 1. EXECUTIVE COMMAND CENTER (HERO) */}
      <motion.div
        custom={0}
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="relative overflow-hidden rounded-[32px] bg-slate-900 p-8 text-white shadow-2xl"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#6C63FF]/20 to-transparent opacity-50" />
        <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[#6C63FF] opacity-20 blur-3xl" />

        <div className="relative z-10">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">
                Welcome back, {user?.name?.split(' ')[0] || 'Partner'}
              </h1>
              <p className="mt-1 text-slate-400">Here is your store performance at a glance.</p>
            </div>
            <div className="flex items-center gap-3">
              {healthData?.healthGrade && (
                <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/10 px-5 py-3 backdrop-blur-md">
                  <ShieldCheck className="h-4 w-4 text-emerald-400" />
                  <span className="text-sm font-semibold tracking-wide text-emerald-400">
                    Health Grade: {healthData.healthGrade}
                  </span>
                </div>
              )}
              {kycStatus === 'APPROVED' && (
                <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur-md">
                  <span className="relative flex h-3 w-3">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
                  </span>
                  <span className="text-sm font-semibold uppercase text-emerald-400">
                    KYC Verified
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="mt-10 grid grid-cols-2 gap-6 lg:grid-cols-4">
            <div className="border-l-2 border-[#6C63FF] pl-4">
              <p className="text-sm font-medium text-slate-400">Total Products</p>
              <h2 className="mt-1 text-3xl font-bold tracking-tight">{totalProducts}</h2>
            </div>
            <div className="border-l-2 border-amber-500 pl-4">
              <p className="text-sm font-medium text-slate-400">Avg. Rating</p>
              <h2 className="mt-1 text-3xl font-bold tracking-tight">
                {avgRating} <span className="text-lg text-amber-400">★</span>
              </h2>
            </div>
            <div className="border-l-2 border-emerald-500 pl-4">
              <p className="text-sm font-medium text-slate-400">Total Orders</p>
              <h2 className="mt-1 text-3xl font-bold tracking-tight">{totalOrders}</h2>
            </div>
            <div className="border-l-2 border-pink-500 pl-4">
              <p className="text-sm font-medium text-slate-400">Lifetime Revenue</p>
              <h2 className="mt-1 text-3xl font-bold tracking-tight">
                {compactInr(totalSalesPaise)}
              </h2>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        {/* LEFT COLUMN (2 cols on XL) */}
        <div className="space-y-6 xl:col-span-2">
          {/* 2. ORDER FULFILLMENT CENTER */}
          <motion.div
            custom={1}
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="rounded-3xl bg-white p-7 shadow-[0_8px_30px_-4px_rgba(0,0,0,0.04)]"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-red-50 text-red-600">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Order Fulfillment</h2>
                <p className="text-sm text-slate-500">Current month performance breakdown</p>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-4">
              {[
                {
                  label: 'New Orders',
                  // The same statuses the summary API counts for this tile.
                  status: 'PENDING_PAYMENT,PLACED',
                  value: newOrdersCount,
                  icon: ClipboardList,
                  color: 'text-indigo-600',
                  bg: 'bg-indigo-50',
                  border: 'border-indigo-100',
                },
                {
                  label: 'Cancelled',
                  status: 'CANCELLED',
                  value: cancelledOrdersCount,
                  icon: XCircle,
                  color: 'text-rose-600',
                  bg: 'bg-rose-50',
                  border: 'border-rose-100',
                },
                {
                  label: 'Processing',
                  status: 'CONFIRMED,PACKED,SHIPPED',
                  value: onDeliveryCount,
                  icon: Truck,
                  color: 'text-amber-600',
                  bg: 'bg-amber-50',
                  border: 'border-amber-100',
                },
                {
                  label: 'Delivered',
                  status: 'DELIVERED',
                  value: deliveredCount,
                  icon: CheckCircle2,
                  color: 'text-emerald-600',
                  bg: 'bg-emerald-50',
                  border: 'border-emerald-100',
                },
              ].map((item, idx) => (
                <button
                  type="button"
                  key={idx}
                  onClick={() =>
                    setDrill({
                      label: item.label,
                      status: item.status,
                      from: monthStart,
                      count: item.value,
                    })
                  }
                  title={`See ${item.label.toLowerCase()} this month`}
                  className={`group relative w-full cursor-pointer overflow-hidden rounded-2xl border ${item.border} p-4 text-left transition-all hover:-translate-y-1 hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-400`}
                >
                  <div className={`mb-3 inline-block rounded-lg ${item.bg} p-2 ${item.color}`}>
                    <item.icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-2xl font-black">{item.value}</h3>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {item.label}
                  </p>
                </button>
              ))}
            </div>
          </motion.div>

          {/* 2b. SHIPMENTS
              Separate from Order Fulfillment above on purpose. That panel reports
              the sub-order's state; this one reports the parcel's. They can
              legitimately disagree — an order marked SHIPPED whose courier has
              not scanned a pickup is exactly the gap worth seeing — so merging
              them would hide the discrepancy rather than explain it. */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="rounded-3xl bg-white p-7 shadow-[0_8px_30px_-4px_rgba(0,0,0,0.04)]"
          >
            {/* Header */}
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-sky-50 text-sky-600">
                <Truck className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Shipments</h2>
                <p className="text-sm text-slate-500">Where your parcels are right now</p>
              </div>
            </div>

            {/* Status tiles — each with a unique accent color to match Order Fulfillment design */}
            <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
              {[
                {
                  label: 'Pending',
                  keys: ['PENDING'],
                  icon: ClipboardList,
                  color: 'text-violet-600',
                  bg: 'bg-violet-50',
                  border: 'border-violet-100',
                  valueColor: 'text-violet-700',
                },
                {
                  label: 'AWB Assigned',
                  keys: ['AWB_ASSIGNED'],
                  icon: Package,
                  color: 'text-blue-600',
                  bg: 'bg-blue-50',
                  border: 'border-blue-100',
                  valueColor: 'text-blue-700',
                },
                {
                  label: 'Pickup',
                  keys: ['PICKUP_SCHEDULED', 'PICKED_UP'],
                  icon: MapPin,
                  color: 'text-cyan-600',
                  bg: 'bg-cyan-50',
                  border: 'border-cyan-100',
                  valueColor: 'text-cyan-700',
                },
                {
                  label: 'In Transit',
                  keys: ['IN_TRANSIT'],
                  icon: Truck,
                  color: 'text-indigo-600',
                  bg: 'bg-indigo-50',
                  border: 'border-indigo-100',
                  valueColor: 'text-indigo-700',
                },
                {
                  label: 'Out for Delivery',
                  keys: ['OUT_FOR_DELIVERY'],
                  icon: Navigation,
                  color: 'text-amber-600',
                  bg: 'bg-amber-50',
                  border: 'border-amber-100',
                  valueColor: 'text-amber-700',
                },
                {
                  label: 'Delivered',
                  keys: ['DELIVERED'],
                  icon: CheckCircle2,
                  color: 'text-emerald-600',
                  bg: 'bg-emerald-50',
                  border: 'border-emerald-100',
                  valueColor: 'text-emerald-700',
                },
                {
                  label: 'NDR / RTO',
                  keys: ['UNDELIVERED', 'RTO_INITIATED', 'RTO_DELIVERED'],
                  icon: AlertTriangle,
                  color: 'text-rose-600',
                  bg: 'bg-rose-50',
                  border: 'border-rose-200',
                  valueColor: 'text-rose-600',
                  attention: true,
                },
                {
                  label: 'Cancelled / Failed',
                  keys: ['CANCELLED', 'FAILED'],
                  icon: XCircle,
                  color: 'text-rose-700',
                  bg: 'bg-rose-50',
                  border: 'border-rose-200',
                  valueColor: 'text-rose-700',
                  attention: true,
                },
              ].map((tile) => {
                const value = tile.keys.reduce(
                  (n, key) => n + (summary?.shipments.counts[key] ?? 0),
                  0,
                );
                const isAlert = tile.attention && value > 0;
                return (
                  <button
                    type="button"
                    key={tile.label}
                    onClick={() =>
                      setDrill({
                        label: tile.label,
                        shipmentStatus: tile.keys.join(','),
                        count: value,
                      })
                    }
                    title={`See ${tile.label.toLowerCase()} shipments`}
                    className={`group relative w-full cursor-pointer overflow-hidden rounded-2xl border ${tile.border} p-4 text-left transition-all hover:-translate-y-1 hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-400 ${isAlert ? 'ring-1 ring-rose-300' : ''}`}
                  >
                    <div className={`mb-3 inline-block rounded-lg ${tile.bg} p-2 ${tile.color}`}>
                      <tile.icon className="h-5 w-5" />
                    </div>
                    <h3 className={`text-2xl font-black ${tile.valueColor}`}>{value}</h3>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {tile.label}
                    </p>
                  </button>
                );
              })}
            </div>

            {/* Recent Shipments */}
            <div className="mt-6">
              <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-400">
                Recent Shipments
              </h3>
              {(summary?.shipments.recent ?? []).length === 0 ? (
                <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 p-8 text-center">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-sky-50 text-sky-400">
                    <Truck className="h-5 w-5" />
                  </div>
                  <p className="text-sm font-medium text-slate-500">No shipments yet.</p>
                  <p className="text-xs text-slate-400">
                    They appear here once an order is packed and booked.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-slate-100">
                  <table className="w-full min-w-[560px] text-left text-sm">
                    <thead className="bg-slate-50">
                      <tr className="text-xs uppercase tracking-wide text-slate-400">
                        <th className="px-4 py-3 font-bold">Order</th>
                        <th className="px-4 py-3 font-bold">AWB</th>
                        <th className="px-4 py-3 font-bold">Courier</th>
                        <th className="px-4 py-3 font-bold">Status</th>
                        <th className="px-4 py-3 text-right font-bold">Updated</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(summary?.shipments.recent ?? []).map((s) => (
                        <tr
                          key={s.id}
                          onClick={() => navigate(`/orders/${s.subOrderId}`)}
                          title="Open this order"
                          className="cursor-pointer transition-colors hover:bg-slate-50/70"
                        >
                          <td className="px-4 py-3 font-semibold text-slate-800">
                            {s.orderNumber}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-slate-500">
                            {s.awbNumber ?? '—'}
                          </td>
                          <td className="px-4 py-3 text-slate-600">{s.courierName ?? '—'}</td>
                          <td className="px-4 py-3">
                            <span
                              className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                                ATTENTION_STATUSES.has(s.status)
                                  ? 'bg-rose-100 text-rose-700'
                                  : s.status === 'DELIVERED'
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : s.status === 'IN_TRANSIT' || s.status === 'OUT_FOR_DELIVERY'
                                      ? 'bg-indigo-100 text-indigo-700'
                                      : 'bg-sky-100 text-sky-700'
                              }`}
                            >
                              {s.status.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-xs text-slate-400">
                            {new Date(s.updatedAt).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </motion.div>

          {/* 3. REVENUE INTELLIGENCE */}
          <motion.div
            custom={2}
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="rounded-3xl bg-white p-7 shadow-[0_8px_30px_-4px_rgba(0,0,0,0.04)]"
          >
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold">Revenue Intelligence</h2>
                <p className="text-sm text-slate-500">Last 6 months performance</p>
              </div>
              <div className="flex items-center gap-2 rounded-xl bg-slate-50 px-4 py-2">
                <TrendingUp className="h-4 w-4 text-emerald-600" />
                <span className="text-sm font-bold text-emerald-600">
                  This Month: {formatPaise(currentMonthSalesPaise)}
                </span>
              </div>
            </div>

            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6C63FF" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6C63FF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    tickFormatter={(val) => compactInr(val)}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: '16px',
                      border: 'none',
                      boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)',
                    }}
                    formatter={(val: number) => [formatPaise(val), 'Revenue']}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#6C63FF"
                    strokeWidth={3}
                    fillOpacity={1}
                    fill="url(#colorRevenue)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-4 border-t border-slate-100 pt-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Current Month
                </p>
                <h4 className="mt-1 text-xl font-bold text-slate-900">
                  {formatPaise(currentMonthSalesPaise)}
                </h4>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Last Month
                </p>
                <h4 className="mt-1 text-xl font-bold text-slate-500">
                  {formatPaise(lastMonthSalesPaise)}
                </h4>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Reviews
                </p>
                <h4 className="mt-1 text-xl font-bold text-amber-600">{reviewCount} reviews</h4>
              </div>
            </div>
          </motion.div>

          {/* 5. OPERATIONS CONTROL CENTER (Quick Actions) */}
          <motion.div
            custom={3}
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="rounded-3xl bg-white p-7 shadow-[0_8px_30px_-4px_rgba(0,0,0,0.04)]"
          >
            <div className="mb-6">
              <h2 className="text-lg font-bold">Operations Control Center</h2>
              <p className="text-sm text-slate-500">Manage all aspects of your store</p>
            </div>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              {quickActions.map((ws) => (
                <Link
                  key={ws.label}
                  to={ws.to}
                  className="group relative flex flex-col items-center rounded-2xl border border-slate-100 bg-slate-50 p-5 text-center transition-all hover:-translate-y-1 hover:border-transparent hover:bg-white hover:shadow-[0_10px_30px_-10px_rgba(0,0,0,0.1)]"
                >
                  <div
                    className={`mb-3 grid h-12 w-12 place-items-center rounded-xl ${ws.bg} ${ws.color} transition-transform group-hover:scale-110 group-hover:shadow-md`}
                  >
                    <ws.Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-900">{ws.label}</h3>
                  <p className="mt-1 text-[11px] font-medium text-slate-500">{ws.desc}</p>
                </Link>
              ))}
            </div>
          </motion.div>
        </div>

        {/* RIGHT COLUMN: Sidebar panels */}
        <div className="space-y-6">
          {/* 4. CATEGORY DISTRIBUTION */}
          <motion.div
            custom={4}
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="rounded-3xl bg-white p-7 shadow-[0_8px_30px_-4px_rgba(0,0,0,0.04)]"
          >
            <h2 className="text-lg font-bold">Product Categories</h2>
            {categoryCounts.length > 0 ? (
              <>
                <div className="relative mx-auto mt-6 h-48 w-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Tooltip
                        cursor={{ fill: 'transparent' }}
                        contentStyle={{
                          borderRadius: '12px',
                          border: 'none',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        }}
                      />
                      <Pie
                        data={categoryCounts}
                        dataKey="count"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={65}
                        outerRadius={90}
                        paddingAngle={5}
                        stroke="none"
                      >
                        {categoryCounts.map((_c, i) => (
                          <Cell key={i} fill={categoryColors[i % categoryColors.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-xs font-bold uppercase text-slate-400">Products</span>
                    <span className="text-xl font-extrabold">{totalProducts}</span>
                  </div>
                </div>
                <div className="mt-6 space-y-4">
                  {categoryCounts.map((cat, i) => (
                    <div key={cat.name}>
                      <div className="flex justify-between text-sm font-semibold">
                        <span className="flex items-center gap-2">
                          <div
                            className="h-2 w-2 rounded-full"
                            style={{ backgroundColor: categoryColors[i % categoryColors.length] }}
                          />
                          {cat.name}
                        </span>
                        <span>{cat.count}</span>
                      </div>
                      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                        <div
                          className="h-full rounded-full transition-all duration-1000"
                          style={{
                            width: `${cat.percentage}%`,
                            backgroundColor: categoryColors[i % categoryColors.length],
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="mt-4 text-sm text-slate-500">
                No products listed yet. Add products to see category distribution.
              </p>
            )}
          </motion.div>

          {/* KYC STATUS BADGE */}
          <motion.div
            custom={5}
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="rounded-3xl bg-white p-7 shadow-[0_8px_30px_-4px_rgba(0,0,0,0.04)]"
          >
            <h2 className="mb-5 text-lg font-bold">Compliance Status</h2>
            {kycStatus === 'APPROVED' ? (
              <div className="flex items-center justify-between rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-100 text-emerald-600">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-emerald-800">KYC Verified Seller</h4>
                    <p className="text-xs text-emerald-600">Full platform access enabled</p>
                  </div>
                </div>
                <Badge tone="success">COMPLIANT</Badge>
              </div>
            ) : kycStatus === 'SUBMITTED' || kycStatus === 'UNDER_REVIEW' ? (
              <div className="flex animate-pulse items-center justify-between rounded-2xl border border-amber-200 bg-amber-50/50 p-5">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-amber-100 text-amber-600">
                    <AlertTriangle className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-amber-800">Review In Progress</h4>
                    <p className="text-xs text-amber-600">Typically takes 24 hours</p>
                  </div>
                </div>
                <Badge tone="warning">PENDING</Badge>
              </div>
            ) : (
              <div className="rounded-2xl border border-red-200 bg-red-50/50 p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-red-100 text-red-600">
                      <AlertTriangle className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-red-800">KYC Not Verified</h4>
                      <p className="text-xs text-red-600">Submit compliance documents</p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/onboarding')}
                  className="mt-4 w-full rounded-xl bg-red-600 py-2.5 text-sm font-bold text-white transition-colors hover:bg-red-700"
                >
                  Verify Now →
                </button>
              </div>
            )}
          </motion.div>

          {/* TOP PRODUCTS SHELF */}
          <motion.div
            custom={6}
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="rounded-3xl bg-slate-900 p-7 text-white shadow-[0_8px_30px_-4px_rgba(0,0,0,0.04)]"
          >
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-lg font-bold">Top Products</h2>
              <Link
                to="/products/new"
                className="flex items-center gap-1 rounded-lg bg-white/10 px-3 py-1.5 text-xs font-bold text-white/80 transition-colors hover:bg-white/20"
              >
                <Plus className="h-3 w-3" /> Add Product
              </Link>
            </div>
            {products.length === 0 ? (
              <p className="text-sm text-slate-400">No products listed yet.</p>
            ) : (
              <div className="space-y-3">
                {products.slice(0, 5).map((p: any) => {
                  const defaultVariant = p.variants?.[0];
                  return (
                    <div
                      key={p.id}
                      className="flex items-center gap-3 rounded-2xl border border-white/5 bg-white/10 p-3 transition-colors hover:bg-white/15"
                    >
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/10 bg-white/5">
                        {p.media?.[0]?.url ? (
                          <img
                            src={p.media[0].url}
                            alt={p.title}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <Box className="h-4 w-4 text-white/30" />
                        )}
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-xs font-bold text-white/90">{p.title}</span>
                        <span className="text-[10px] text-white/50">
                          {p.category?.name ?? 'General'}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="block text-xs font-bold text-white">
                          {defaultVariant ? formatPaise(defaultVariant.pricePaise) : '—'}
                        </span>
                        <Badge tone={PRODUCT_STATUS_TONE[p.status] ?? 'neutral'}>{p.status}</Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </div>
      </div>

      <DashboardOrdersDrawer drill={drill} onClose={() => setDrill(null)} />
    </div>
  );
}
